import { TemplateUserValue } from 'shared/types';
import { FieldTypes, SiteGroup, Status } from 'shared/enums';
import { PropertyDocumentOrderProject } from 'app/common/types/PropertyDocumentOrderProject';

export const propertyDocumentProject: Record<keyof PropertyDocumentOrderProject, any> = {
	group: 1,
	entityID: 1,
	entity: 1,
	mailingDate: 1,
	order: {
		$arrayElemAt: [
			{
				$filter: {
					input: '$status',
					as: 'completed',
					cond: { $eq: ['$$completed.label', Status.Order] },
				},
			},
			-1,
		],
	},
	completed: {
		$arrayElemAt: [
			{
				$filter: {
					input: '$status',
					as: 'status',
					cond: { $and: [{ $eq: ['$$status.label', Status.Completed] }, { $eq: ['$$status.group', SiteGroup.PropertyDocument] }] },
				},
			},
			-1,
		],
	},
	issue: {
		$arrayElemAt: [
			{
				$filter: {
					input: '$status',
					as: 'status',
					cond: { $and: [{ $eq: ['$$status.label', Status.Issue] }, { $eq: ['$$status.group', SiteGroup.PropertyDocument] }] },
				},
			},
			-1,
		],
	},
	partialRefunds: {
		$filter: {
			input: '$status',
			as: 'partialRefund',
			cond: { $eq: ['$$partialRefund.label', Status.PartialRefund] },
		},
	},
	refund: {
		$arrayElemAt: [
			{
				$filter: {
					input: '$status',
					as: 'refund',
					cond: { $eq: ['$$refund.label', Status.Refund] },
				},
			},
			-1,
		],
	},
	refundRequested: {
		$arrayElemAt: [
			{
				$filter: {
					input: '$status',
					as: 'resend',
					cond: { $eq: ['$$resend.label', Status.RefundRequested] },
				},
			},
			-1,
		],
	},
	refundStatus: 1,
	website: 1,
	product: 1,
	productTitle: 1,
	email: 1,
	phone: 1,
	price: 1,
	source: 1,
	shipping: 1,
	quantity: 1,
	transactionID: 1,
	paymentMethod: 1,
	paymentMerchant: 1,
	merchantReference: 1,
	ccProcessingFee: 1,
	refundRequestStatus: 1,
	currentStatus: 1,
	_id: 1,
};

const propertyDocumentTemplateOptions = {
	businessID: {
		label: 'BusinessID',
		param: '{{businessID}}',
		path: 'businessID',
		type: FieldTypes.Text,
	},
	company: {
		label: 'Company',
		param: '{{company}}',
		path: 'company',
		type: FieldTypes.Text,
	},
	customerName: {
		label: 'Customer Name',
		param: '{{customerName}}',
		path: 'customerName',
		type: FieldTypes.Text,
	},
	transactionID: {
		label: 'Transaction ID',
		param: '{{transactionID}}',
		path: 'transactionID',
		type: FieldTypes.Text,
	},
	merchantReference: {
		label: 'MerchantReference',
		param: '{{merchantReference}}',
		path: 'merchantReference',
		type: FieldTypes.Text,
	},
	address: {
		label: 'Address',
		param: '{{address}}',
		path: 'address',
		type: FieldTypes.Text,
	},
	city: {
		label: 'City',
		param: '{{city}}',
		path: 'city',
		type: FieldTypes.Text,
	},
	state: {
		label: 'State',
		param: '{{state}}',
		path: 'state',
		type: FieldTypes.Text,
	},
	zip: {
		label: 'Zip',
		param: '{{zip}}',
		path: 'zip',
		type: FieldTypes.Text,
	},
	orderDate: {
		label: 'Order Date',
		param: '{{orderDate}}',
		path: 'orderDate',
		type: FieldTypes.Date,
	},
	completedDate: {
		label: 'Completed Date',
		param: '{{completedDate}}',
		path: 'completedDate',
		type: FieldTypes.Date,
	},
	refundDate: {
		label: 'Refund Date',
		param: '{{refundDate}}',
		path: 'refundDate',
		type: FieldTypes.Date,
	},
	total: {
		label: 'Order Total',
		param: '{{total}}',
		path: 'total',
		type: FieldTypes.Currency,
	},
	invoiceLink: {
		label: 'Declined / Invoice Link',
		param: '{{invoiceLink}}',
		path: 'invoiceLink',
		type: FieldTypes.Link,
	},
	refundLink: {
		label: 'Refund Link',
		param: '{{refundLink}}',
		path: 'refundLink',
		type: FieldTypes.Link,
	},
	externalLink: {
		label: 'External Link',
		param: '{{externalLink}}',
		path: 'externalLink',
		type: FieldTypes.Link,
	},
	cardNo: {
		label: 'Card No',
		param: '{{cardNo}}',
		path: 'cardNo',
		type: FieldTypes.Text,
	},
	cardType: {
		label: 'Card Type',
		param: '{{cardType}}',
		path: 'cardType',
		type: FieldTypes.Text,
	},
	refundedItemsName: {
		label: 'Refunded Items Name',
		param: '{{refundedItemsName}}',
		path: 'refundedItemsName',
		type: FieldTypes.Text,
	},
	refundedAmount: {
		label: 'Refunded Amount',
		param: '{{refundedAmount}}',
		path: 'refundedAmount',
		type: FieldTypes.Currency,
	},
	product: {
		label: 'Product',
		param: '{{product}}',
		path: 'product',
		type: FieldTypes.Text,
	},
	bankMessage: {
		label: 'Bank Message',
		param: '{{bankMessage}}',
		path: 'bankMessage',
		type: FieldTypes.Text,
	},
	siteName: {
		label: 'Profile Name',
		param: '{{siteName}}',
		path: 'siteName',
		type: FieldTypes.Text,
	},
	siteWebAddress: {
		label: 'Profile Web Address',
		param: '{{siteWebAddress}}',
		path: 'siteWebAddress',
		type: FieldTypes.Text,
	},
	siteEmail: {
		label: 'Profile Email',
		param: '{{siteEmail}}',
		path: 'siteEmail',
		type: FieldTypes.Text,
	},
	sitePhone: {
		label: 'Profile Phone',
		param: '{{sitePhone}}',
		path: 'sitePhone',
		type: FieldTypes.Text,
	},
};

export const propertyDocumentTemplateUserOptions: TemplateUserValue[] = Object.keys(propertyDocumentTemplateOptions).map(
	(key) => propertyDocumentTemplateOptions[key],
);
export type PropertyDocumentTemplateUserOptionsValue = Record<keyof typeof propertyDocumentTemplateOptions, any>;

export const proDocCountyOptions = [
	//FL
	{ name: 'Alachua', abbreviation: 'ALC', state: 'FL' },
	{ name: 'Baker', abbreviation: 'BAK', state: 'FL' },
	{ name: 'Bay', abbreviation: 'BAY', state: 'FL' },
	{ name: 'Bradford', abbreviation: 'BRF', state: 'FL' },
	{ name: 'Brevard', abbreviation: 'BVC', state: 'FL' },
	{ name: 'Broward', abbreviation: 'BRO', state: 'FL' },
	{ name: 'Calhoun', abbreviation: 'CAH', state: 'FL' },
	{ name: 'Charlotte', abbreviation: 'CHA', state: 'FL' },
	{ name: 'Citrus', abbreviation: 'CIT', state: 'FL' },
	{ name: 'Clay', abbreviation: 'CLA', state: 'FL' },
	{ name: 'Collier', abbreviation: 'COL', state: 'FL' },
	{ name: 'Columbia', abbreviation: 'CLB', state: 'FL' },
	{ name: 'DeSoto', abbreviation: 'DES', state: 'FL' },
	{ name: 'Dixie', abbreviation: 'DIX', state: 'FL' },
	{ name: 'Duval', abbreviation: 'DUV', state: 'FL' },
	{ name: 'Escambia', abbreviation: 'ESC', state: 'FL' },
	{ name: 'Flagler', abbreviation: 'FLA', state: 'FL' },
	{ name: 'Franklin', abbreviation: 'FRA', state: 'FL' },
	{ name: 'Gadsden', abbreviation: 'GAD', state: 'FL' },
	{ name: 'Gilchrist', abbreviation: 'GIL', state: 'FL' },
	{ name: 'Glades', abbreviation: 'GLA', state: 'FL' },
	{ name: 'Gulf', abbreviation: 'GUL', state: 'FL' },
	{ name: 'Hamilton', abbreviation: 'HAM', state: 'FL' },
	{ name: 'Hardee', abbreviation: 'HAR', state: 'FL' },
	{ name: 'Hendry', abbreviation: 'HEN', state: 'FL' },
	{ name: 'Hernando', abbreviation: 'HER', state: 'FL' },
	{ name: 'Highlands', abbreviation: 'HIG', state: 'FL' },
	{ name: 'Hillsborough', abbreviation: 'HIL', state: 'FL' },
	{ name: 'Holmes', abbreviation: 'HOL', state: 'FL' },
	{ name: 'Indian River', abbreviation: 'IRC', state: 'FL' },
	{ name: 'Jackson', abbreviation: 'JAC', state: 'FL' },
	{ name: 'Jefferson', abbreviation: 'JEF', state: 'FL' },
	{ name: 'Lafayette', abbreviation: 'LAF', state: 'FL' },
	{ name: 'Lake', abbreviation: 'LKC', state: 'FL' },
	{ name: 'Lee', abbreviation: 'LEE', state: 'FL' },
	{ name: 'Leon', abbreviation: 'LEO', state: 'FL' },
	{ name: 'Levy', abbreviation: 'LEV', state: 'FL' },
	{ name: 'Liberty', abbreviation: 'LIB', state: 'FL' },
	{ name: 'Madison', abbreviation: 'MAD', state: 'FL' },
	{ name: 'Manatee', abbreviation: 'MAN', state: 'FL' },
	{ name: 'Marion', abbreviation: 'MAR', state: 'FL' },
	{ name: 'Martin', abbreviation: 'MRT', state: 'FL' },
	{ name: 'Miami-Dade', abbreviation: 'MIA', state: 'FL' },
	{ name: 'Monroe', abbreviation: 'MNO', state: 'FL' },
	{ name: 'Nassau', abbreviation: 'NAS', state: 'FL' },
	{ name: 'Okaloosa', abbreviation: 'OKA', state: 'FL' },
	{ name: 'Okeechobee', abbreviation: 'OKE', state: 'FL' },
	{ name: 'Orange', abbreviation: 'ORC', state: 'FL' },
	{ name: 'Osceola', abbreviation: 'OSC', state: 'FL' },
	{ name: 'Palm Beach', abbreviation: 'PAL', state: 'FL' },
	{ name: 'Pasco', abbreviation: 'PSC', state: 'FL' },
	{ name: 'Pinellas', abbreviation: 'PIN', state: 'FL' },
	{ name: 'Polk', abbreviation: 'PKC', state: 'FL' },
	{ name: 'Putnam', abbreviation: 'PUT', state: 'FL' },
	{ name: 'St. Johns', abbreviation: 'STJ', state: 'FL' },
	{ name: 'St. Lucie', abbreviation: 'STL', state: 'FL' },
	{ name: 'Santa Rosa', abbreviation: 'SRO', state: 'FL' },
	{ name: 'Sarasota', abbreviation: 'SAC', state: 'FL' },
	{ name: 'Seminole', abbreviation: 'SEM', state: 'FL' },
	{ name: 'Sumter', abbreviation: 'SUM', state: 'FL' },
	{ name: 'Suwannee', abbreviation: 'SUW', state: 'FL' },
	{ name: 'Taylor', abbreviation: 'TAY', state: 'FL' },
	{ name: 'Union', abbreviation: 'UNI', state: 'FL' },
	{ name: 'Volusia', abbreviation: 'VOL', state: 'FL' },
	{ name: 'Wakulla', abbreviation: 'WAK', state: 'FL' },
	{ name: 'Walton', abbreviation: 'WAL', state: 'FL' },
	{ name: 'Washington', abbreviation: 'WAS', state: 'FL' },
	//CA
	{ name: 'Alameda', abbreviation: 'AMD', state: 'CA' },
	{ name: 'Alpine', abbreviation: 'ALP', state: 'CA' },
	{ name: 'Amador', abbreviation: 'AMR', state: 'CA' },
	{ name: 'Butte', abbreviation: 'BTE', state: 'CA' },
	{ name: 'Calaveras', abbreviation: 'CVS', state: 'CA' },
	{ name: 'Colusa', abbreviation: 'CLS', state: 'CA' },
	{ name: 'Contra Costa', abbreviation: 'CCA', state: 'CA' },
	{ name: 'Del Norte', abbreviation: 'DNE', state: 'CA' },
	{ name: 'El Dorado', abbreviation: 'EDO', state: 'CA' },
	{ name: 'Fresno', abbreviation: 'FSO', state: 'CA' },
	{ name: 'Glenn', abbreviation: 'GEN', state: 'CA' },
	{ name: 'Humboldt', abbreviation: 'HBT', state: 'CA' },
	{ name: 'Imperial', abbreviation: 'IPL', state: 'CA' },
	{ name: 'Inyo', abbreviation: 'INY', state: 'CA' },
	{ name: 'Kern', abbreviation: 'KRN', state: 'CA' },
	{ name: 'Kings', abbreviation: 'KNS', state: 'CA' },
	{ name: 'Lake', abbreviation: 'LAK', state: 'CA' },
	{ name: 'Lassen', abbreviation: 'LSN', state: 'CA' },
	{ name: 'Los Angeles', abbreviation: 'LAS', state: 'CA' },
	{ name: 'Madera', abbreviation: 'MDA', state: 'CA' },
	{ name: 'Marin', abbreviation: 'MRN', state: 'CA' },
	{ name: 'Mariposa', abbreviation: 'MPA', state: 'CA' },
	{ name: 'Mendocino', abbreviation: 'MDO', state: 'CA' },
	{ name: 'Merced', abbreviation: 'MCD', state: 'CA' },
	{ name: 'Modoc', abbreviation: 'MDC', state: 'CA' },
	{ name: 'Mono', abbreviation: 'MON', state: 'CA' },
	{ name: 'Monterey', abbreviation: 'MTY', state: 'CA' },
	{ name: 'Napa', abbreviation: 'NPA', state: 'CA' },
	{ name: 'Nevada', abbreviation: 'NVD', state: 'CA' },
	{ name: 'Orange', abbreviation: 'ORG', state: 'CA' },
	{ name: 'Placer', abbreviation: 'PCR', state: 'CA' },
	{ name: 'Plumas', abbreviation: 'PMS', state: 'CA' },
	{ name: 'Riverside', abbreviation: 'RSE', state: 'CA' },
	{ name: 'Sacramento', abbreviation: 'SMO', state: 'CA' },
	{ name: 'San Benito', abbreviation: 'SBO', state: 'CA' },
	{ name: 'San Bernardino', abbreviation: 'SBN', state: 'CA' },
	{ name: 'San Diego', abbreviation: 'SDG', state: 'CA' },
	{ name: 'San Francisco', abbreviation: 'SFO', state: 'CA' },
	{ name: 'San Joaquin', abbreviation: 'SJN', state: 'CA' },
	{ name: 'San Luis Obispo', abbreviation: 'SLO', state: 'CA' },
	{ name: 'San Mateo', abbreviation: 'SME', state: 'CA' },
	{ name: 'Santa Barbara', abbreviation: 'SBA', state: 'CA' },
	{ name: 'Santa Clara', abbreviation: 'SCA', state: 'CA' },
	{ name: 'Santa Cruz', abbreviation: 'SCZ', state: 'CA' },
	{ name: 'Shasta', abbreviation: 'SST', state: 'CA' },
	{ name: 'Sierra', abbreviation: 'SRA', state: 'CA' },
	{ name: 'Siskiyou', abbreviation: 'SKY', state: 'CA' },
	{ name: 'Solano', abbreviation: 'SLN', state: 'CA' },
	{ name: 'Sonoma', abbreviation: 'SNM', state: 'CA' },
	{ name: 'Stanislaus', abbreviation: 'SLU', state: 'CA' },
	{ name: 'Sutter', abbreviation: 'STR', state: 'CA' },
	{ name: 'Tehama', abbreviation: 'TMA', state: 'CA' },
	{ name: 'Trinity', abbreviation: 'TRY', state: 'CA' },
	{ name: 'Tulare', abbreviation: 'TLR', state: 'CA' },
	{ name: 'Tuolumne', abbreviation: 'TLN', state: 'CA' },
	{ name: 'Ventura', abbreviation: 'VTR', state: 'CA' },
	{ name: 'Yolo', abbreviation: 'YOL', state: 'CA' },
	{ name: 'Yuba', abbreviation: 'YBA', state: 'CA' },
	//TX
	{ name: 'Anderson', abbreviation: 'AND', state: 'TX' },
	{ name: 'Andrews', abbreviation: 'ANS', state: 'TX' },
	{ name: 'Angelina', abbreviation: 'ANG', state: 'TX' },
	{ name: 'Aransas', abbreviation: 'ARA', state: 'TX' },
	{ name: 'Archer', abbreviation: 'ARC', state: 'TX' },
	{ name: 'Armstrong', abbreviation: 'ARM', state: 'TX' },
	{ name: 'Atascosa', abbreviation: 'ATA', state: 'TX' },
	{ name: 'Austin', abbreviation: 'AUS', state: 'TX' },
	{ name: 'Bailey', abbreviation: 'BAI', state: 'TX' },
	{ name: 'Bandera', abbreviation: 'BAN', state: 'TX' },
	{ name: 'Bastrop', abbreviation: 'BAS', state: 'TX' },
	{ name: 'Baylor', abbreviation: 'BAR', state: 'TX' },
	{ name: 'Bee', abbreviation: 'BEE', state: 'TX' },
	{ name: 'Bell', abbreviation: 'BEL', state: 'TX' },
	{ name: 'Bexar', abbreviation: 'BEX', state: 'TX' },
	{ name: 'Blanco', abbreviation: 'BLA', state: 'TX' },
	{ name: 'Borden', abbreviation: 'BOR', state: 'TX' },
	{ name: 'Bosque', abbreviation: 'BOS', state: 'TX' },
	{ name: 'Bowie', abbreviation: 'BOW', state: 'TX' },
	{ name: 'Brazoria', abbreviation: 'BRA', state: 'TX' },
	{ name: 'Brazos', abbreviation: 'BRS', state: 'TX' },
	{ name: 'Brewster', abbreviation: 'BRE', state: 'TX' },
	{ name: 'Briscoe', abbreviation: 'BRI', state: 'TX' },
	{ name: 'Brooks', abbreviation: 'BKS', state: 'TX' },
	{ name: 'Brown', abbreviation: 'BRN', state: 'TX' },
	{ name: 'Burleson', abbreviation: 'BUR', state: 'TX' },
	{ name: 'Burnet', abbreviation: 'BUT', state: 'TX' },
	{ name: 'Caldwell', abbreviation: 'CAL', state: 'TX' },
	{ name: 'Calhoun', abbreviation: 'CAN', state: 'TX' },
	{ name: 'Callahan', abbreviation: 'CLL', state: 'TX' },
	{ name: 'Cameron', abbreviation: 'CAM', state: 'TX' },
	{ name: 'Camp', abbreviation: 'CAP', state: 'TX' },
	{ name: 'Carson', abbreviation: 'CAR', state: 'TX' },
	{ name: 'Cass', abbreviation: 'CAS', state: 'TX' },
	{ name: 'Castro', abbreviation: 'CAO', state: 'TX' },
	{ name: 'Chambers', abbreviation: 'CHS', state: 'TX' },
	{ name: 'Cherokee', abbreviation: 'CHE', state: 'TX' },
	{ name: 'Childress', abbreviation: 'CHI', state: 'TX' },
	{ name: 'Clay', abbreviation: 'CLY', state: 'TX' },
	{ name: 'Cochran', abbreviation: 'COC', state: 'TX' },
	{ name: 'Coke', abbreviation: 'COK', state: 'TX' },
	{ name: 'Coleman', abbreviation: 'CON', state: 'TX' },
	{ name: 'Collin', abbreviation: 'CIN', state: 'TX' },
	{ name: 'Collingsworth', abbreviation: 'COH', state: 'TX' },
	{ name: 'Colorado', abbreviation: 'COO', state: 'TX' },
	{ name: 'Comal', abbreviation: 'COM', state: 'TX' },
	{ name: 'Comanche', abbreviation: 'COE', state: 'TX' },
	{ name: 'Concho', abbreviation: 'CHO', state: 'TX' },
	{ name: 'Cooke', abbreviation: 'CKE', state: 'TX' },
	{ name: 'Coryell', abbreviation: 'COR', state: 'TX' },
	{ name: 'Cottle', abbreviation: 'COT', state: 'TX' },
	{ name: 'Crane', abbreviation: 'CRA', state: 'TX' },
	{ name: 'Crockett', abbreviation: 'CRO', state: 'TX' },
	{ name: 'Crosby', abbreviation: 'CRY', state: 'TX' },
	{ name: 'Culberson', abbreviation: 'CUL', state: 'TX' },
	{ name: 'Dallam', abbreviation: 'DAL', state: 'TX' },
	{ name: 'Dallas', abbreviation: 'DAS', state: 'TX' },
	{ name: 'Dawson', abbreviation: 'DAW', state: 'TX' },
	{ name: 'Deaf Smith', abbreviation: 'DEA', state: 'TX' },
	{ name: 'Delta', abbreviation: 'DEL', state: 'TX' },
	{ name: 'Denton', abbreviation: 'DEN', state: 'TX' },
	{ name: 'DeWitt', abbreviation: 'DEW', state: 'TX' },
	{ name: 'Dickens', abbreviation: 'DIC', state: 'TX' },
	{ name: 'Dimmit', abbreviation: 'DIM', state: 'TX' },
	{ name: 'Donley', abbreviation: 'DON', state: 'TX' },
	{ name: 'Duval', abbreviation: 'DUL', state: 'TX' },
	{ name: 'Eastland', abbreviation: 'EAS', state: 'TX' },
	{ name: 'Ector', abbreviation: 'ECT', state: 'TX' },
	{ name: 'Edwards', abbreviation: 'EDW', state: 'TX' },
	{ name: 'El Paso', abbreviation: 'ELP', state: 'TX' },
	{ name: 'Ellis', abbreviation: 'ELL', state: 'TX' },
	{ name: 'Erath', abbreviation: 'ERA', state: 'TX' },
	{ name: 'Falls', abbreviation: 'FAL', state: 'TX' },
	{ name: 'Fannin', abbreviation: 'FAN', state: 'TX' },
	{ name: 'Fayette', abbreviation: 'FAY', state: 'TX' },
	{ name: 'Fisher', abbreviation: 'FIS', state: 'TX' },
	{ name: 'Floyd', abbreviation: 'FLO', state: 'TX' },
	{ name: 'Foard', abbreviation: 'FOA', state: 'TX' },
	{ name: 'Fort Bend', abbreviation: 'FOR', state: 'TX' },
	{ name: 'Franklin', abbreviation: 'FRN', state: 'TX' },
	{ name: 'Freestone', abbreviation: 'FRE', state: 'TX' },
	{ name: 'Frio', abbreviation: 'FRI', state: 'TX' },
	{ name: 'Gaines', abbreviation: 'GAI', state: 'TX' },
	{ name: 'Galveston', abbreviation: 'GAL', state: 'TX' },
	{ name: 'Garza', abbreviation: 'GAR', state: 'TX' },
	{ name: 'Gillespie', abbreviation: 'GIE', state: 'TX' },
	{ name: 'Glasscock', abbreviation: 'GLK', state: 'TX' },
	{ name: 'Goliad', abbreviation: 'GOL', state: 'TX' },
	{ name: 'Gonzales', abbreviation: 'GON', state: 'TX' },
	{ name: 'Gray', abbreviation: 'GRA', state: 'TX' },
	{ name: 'Grayson', abbreviation: 'GRN', state: 'TX' },
	{ name: 'Gregg', abbreviation: 'GRE', state: 'TX' },
	{ name: 'Grimes', abbreviation: 'GRI', state: 'TX' },
	{ name: 'Guadalupe', abbreviation: 'GUA', state: 'TX' },
	{ name: 'Hale', abbreviation: 'HAL', state: 'TX' },
	{ name: 'Hall', abbreviation: 'HLL', state: 'TX' },
	{ name: 'Hamilton', abbreviation: 'HAN', state: 'TX' },
	{ name: 'Hansford', abbreviation: 'HAD', state: 'TX' },
	{ name: 'Hardeman', abbreviation: 'HRD', state: 'TX' },
	{ name: 'Hardin', abbreviation: 'HIN', state: 'TX' },
	{ name: 'Harris', abbreviation: 'HAS', state: 'TX' },
	{ name: 'Harrison', abbreviation: 'HON', state: 'TX' },
	{ name: 'Hartley', abbreviation: 'HAY', state: 'TX' },
	{ name: 'Haskell', abbreviation: 'HSK', state: 'TX' },
	{ name: 'Hays', abbreviation: 'HYS', state: 'TX' },
	{ name: 'Hemphill', abbreviation: 'HEM', state: 'TX' },
	{ name: 'Henderson', abbreviation: 'HND', state: 'TX' },
	{ name: 'Hidalgo', abbreviation: 'HID', state: 'TX' },
	{ name: 'Hill', abbreviation: 'HAA', state: 'TX' },
	{ name: 'Hockley', abbreviation: 'HOC', state: 'TX' },
	{ name: 'Hood', abbreviation: 'HOO', state: 'TX' },
	{ name: 'Hopkins', abbreviation: 'HOP', state: 'TX' },
	{ name: 'Houston', abbreviation: 'HOU', state: 'TX' },
	{ name: 'Howard', abbreviation: 'HOW', state: 'TX' },
	{ name: 'Hudspeth', abbreviation: 'HUD', state: 'TX' },
	{ name: 'Hunt', abbreviation: 'HUN', state: 'TX' },
	{ name: 'Hutchinson', abbreviation: 'HUT', state: 'TX' },
	{ name: 'Irion', abbreviation: 'IRI', state: 'TX' },
	{ name: 'Jack', abbreviation: 'JAK', state: 'TX' },
	{ name: 'Jackson', abbreviation: 'JAN', state: 'TX' },
	{ name: 'Jasper', abbreviation: 'JAS', state: 'TX' },
	{ name: 'Jeff Davis', abbreviation: 'JES', state: 'TX' },
	{ name: 'Jefferson', abbreviation: 'JEN', state: 'TX' },
	{ name: 'Jim Hogg', abbreviation: 'JIM', state: 'TX' },
	{ name: 'Jim Wells', abbreviation: 'JIS', state: 'TX' },
	{ name: 'Johnson', abbreviation: 'JOH', state: 'TX' },
	{ name: 'Jones', abbreviation: 'JON', state: 'TX' },
	{ name: 'Karnes', abbreviation: 'KAR', state: 'TX' },
	{ name: 'Kaufman', abbreviation: 'KAU', state: 'TX' },
	{ name: 'Kendall', abbreviation: 'KEN', state: 'TX' },
	{ name: 'Kenedy', abbreviation: 'KEY', state: 'TX' },
	{ name: 'Kent', abbreviation: 'KET', state: 'TX' },
	{ name: 'Kerr', abbreviation: 'KER', state: 'TX' },
	{ name: 'Kimble', abbreviation: 'KIM', state: 'TX' },
	{ name: 'King', abbreviation: 'KIN', state: 'TX' },
	{ name: 'Kinney', abbreviation: 'KIY', state: 'TX' },
	{ name: 'Kleberg', abbreviation: 'KLE', state: 'TX' },
	{ name: 'Knox', abbreviation: 'KNO', state: 'TX' },
	{ name: 'La Salle', abbreviation: 'LAE', state: 'TX' },
	{ name: 'Lamar', abbreviation: 'LAM', state: 'TX' },
	{ name: 'Lamb', abbreviation: 'LAB', state: 'TX' },
	{ name: 'Lampasas', abbreviation: 'LMP', state: 'TX' },
	{ name: 'Lavaca', abbreviation: 'LAV', state: 'TX' },
	{ name: 'Lee', abbreviation: 'LAA', state: 'TX' },
	{ name: 'Leon', abbreviation: 'LEN', state: 'TX' },
	{ name: 'Liberty', abbreviation: 'LIY', state: 'TX' },
	{ name: 'Limestone', abbreviation: 'LIM', state: 'TX' },
	{ name: 'Lipscomb', abbreviation: 'LIP', state: 'TX' },
	{ name: 'Live Oak', abbreviation: 'LIV', state: 'TX' },
	{ name: 'Llano', abbreviation: 'LLA', state: 'TX' },
	{ name: 'Loving', abbreviation: 'LOV', state: 'TX' },
	{ name: 'Lubbock', abbreviation: 'LUB', state: 'TX' },
	{ name: 'Lynn', abbreviation: 'LYN', state: 'TX' },
	{ name: 'Madison', abbreviation: 'MDS', state: 'TX' },
	{ name: 'Marion', abbreviation: 'MAI', state: 'TX' },
	{ name: 'Martin', abbreviation: 'MIN', state: 'TX' },
	{ name: 'Mason', abbreviation: 'MAS', state: 'TX' },
	{ name: 'Matagorda', abbreviation: 'MAT', state: 'TX' },
	{ name: 'Maverick', abbreviation: 'MAV', state: 'TX' },
	{ name: 'McCulloch', abbreviation: 'MCC', state: 'TX' },
	{ name: 'McLennan', abbreviation: 'MCL', state: 'TX' },
	{ name: 'McMullen', abbreviation: 'MCM', state: 'TX' },
	{ name: 'Medina', abbreviation: 'MED', state: 'TX' },
	{ name: 'Menard', abbreviation: 'MEN', state: 'TX' },
	{ name: 'Midland', abbreviation: 'MID', state: 'TX' },
	{ name: 'Milam', abbreviation: 'MIL', state: 'TX' },
	{ name: 'Mills', abbreviation: 'MIS', state: 'TX' },
	{ name: 'Mitchell', abbreviation: 'MIT', state: 'TX' },
	{ name: 'Montague', abbreviation: 'MOE', state: 'TX' },
	{ name: 'Montgomery', abbreviation: 'MOY', state: 'TX' },
	{ name: 'Moore', abbreviation: 'MOO', state: 'TX' },
	{ name: 'Morris', abbreviation: 'MOR', state: 'TX' },
	{ name: 'Motley', abbreviation: 'MOT', state: 'TX' },
	{ name: 'Nacogdoches', abbreviation: 'NAC', state: 'TX' },
	{ name: 'Navarro', abbreviation: 'NAV', state: 'TX' },
	{ name: 'Newton', abbreviation: 'NEW', state: 'TX' },
	{ name: 'Nolan', abbreviation: 'NOL', state: 'TX' },
	{ name: 'Nueces', abbreviation: 'NUE', state: 'TX' },
	{ name: 'Ochiltree', abbreviation: 'OCH', state: 'TX' },
	{ name: 'Oldham', abbreviation: 'OLD', state: 'TX' },
	{ name: 'Orange', abbreviation: 'ORA', state: 'TX' },
	{ name: 'Palo Pinto', abbreviation: 'PAO', state: 'TX' },
	{ name: 'Panola', abbreviation: 'PAN', state: 'TX' },
	{ name: 'Parker', abbreviation: 'PAR', state: 'TX' },
	{ name: 'Parmer', abbreviation: 'PER', state: 'TX' },
	{ name: 'Pecos', abbreviation: 'PEC', state: 'TX' },
	{ name: 'Polk', abbreviation: 'POL', state: 'TX' },
	{ name: 'Potter', abbreviation: 'POT', state: 'TX' },
	{ name: 'Presidio', abbreviation: 'PRE', state: 'TX' },
	{ name: 'Rains', abbreviation: 'RAI', state: 'TX' },
	{ name: 'Randall', abbreviation: 'RAN', state: 'TX' },
	{ name: 'Reagan', abbreviation: 'REA', state: 'TX' },
	{ name: 'Real', abbreviation: 'REL', state: 'TX' },
	{ name: 'Red River', abbreviation: 'RED', state: 'TX' },
	{ name: 'Reeves', abbreviation: 'REE', state: 'TX' },
	{ name: 'Refugio', abbreviation: 'REF', state: 'TX' },
	{ name: 'Roberts', abbreviation: 'ROB', state: 'TX' },
	{ name: 'Robertson', abbreviation: 'RON', state: 'TX' },
	{ name: 'Rockwall', abbreviation: 'ROC', state: 'TX' },
	{ name: 'Runnels', abbreviation: 'RUN', state: 'TX' },
	{ name: 'Rusk', abbreviation: 'RUS', state: 'TX' },
	{ name: 'Sabine', abbreviation: 'SAB', state: 'TX' },
	{ name: 'San Augustine', abbreviation: 'SAN', state: 'TX' },
	{ name: 'San Jacinto', abbreviation: 'SAO', state: 'TX' },
	{ name: 'San Patricio', abbreviation: 'SIO', state: 'TX' },
	{ name: 'San Saba', abbreviation: 'SAA', state: 'TX' },
	{ name: 'Schleicher', abbreviation: 'SCH', state: 'TX' },
	{ name: 'Scurry', abbreviation: 'SCU', state: 'TX' },
	{ name: 'Shackelford', abbreviation: 'SHA', state: 'TX' },
	{ name: 'Shelby', abbreviation: 'SHE', state: 'TX' },
	{ name: 'Sherman', abbreviation: 'SHN', state: 'TX' },
	{ name: 'Smith', abbreviation: 'SMI', state: 'TX' },
	{ name: 'Somervell', abbreviation: 'SOM', state: 'TX' },
	{ name: 'Starr', abbreviation: 'STA', state: 'TX' },
	{ name: 'Stephens', abbreviation: 'STE', state: 'TX' },
	{ name: 'Sterling', abbreviation: 'STG', state: 'TX' },
	{ name: 'Stonewall', abbreviation: 'STO', state: 'TX' },
	{ name: 'Sutton', abbreviation: 'SUT', state: 'TX' },
	{ name: 'Swisher', abbreviation: 'SWI', state: 'TX' },
	{ name: 'Tarrant', abbreviation: 'TAR', state: 'TX' },
	{ name: 'Taylor', abbreviation: 'TOR', state: 'TX' },
	{ name: 'Terrell', abbreviation: 'TER', state: 'TX' },
	{ name: 'Terry', abbreviation: 'TEY', state: 'TX' },
	{ name: 'Throckmorton', abbreviation: 'THR', state: 'TX' },
	{ name: 'Titus', abbreviation: 'TIT', state: 'TX' },
	{ name: 'Tom Green', abbreviation: 'TOM', state: 'TX' },
	{ name: 'Travis', abbreviation: 'TRA', state: 'TX' },
	{ name: 'Trinity', abbreviation: 'TRI', state: 'TX' },
	{ name: 'Tyler', abbreviation: 'TYL', state: 'TX' },
	{ name: 'Upshur', abbreviation: 'UPS', state: 'TX' },
	{ name: 'Upton', abbreviation: 'UPT', state: 'TX' },
	{ name: 'Uvalde', abbreviation: 'UVA', state: 'TX' },
	{ name: 'Val Verde', abbreviation: 'VAL', state: 'TX' },
	{ name: 'Van Zandt', abbreviation: 'VAN', state: 'TX' },
	{ name: 'Victoria', abbreviation: 'VIC', state: 'TX' },
	{ name: 'Walker', abbreviation: 'WAR', state: 'TX' },
	{ name: 'Waller', abbreviation: 'WER', state: 'TX' },
	{ name: 'Ward', abbreviation: 'WAD', state: 'TX' },
	{ name: 'Washington', abbreviation: 'WAN', state: 'TX' },
	{ name: 'Webb', abbreviation: 'WEB', state: 'TX' },
	{ name: 'Wharton', abbreviation: 'WHA', state: 'TX' },
	{ name: 'Wheeler', abbreviation: 'WHE', state: 'TX' },
	{ name: 'Wichita', abbreviation: 'WIC', state: 'TX' },
	{ name: 'Wilbarger', abbreviation: 'WIL', state: 'TX' },
	{ name: 'Willacy', abbreviation: 'WIY', state: 'TX' },
	{ name: 'Williamson', abbreviation: 'WIN', state: 'TX' },
	{ name: 'Wilson', abbreviation: 'WON', state: 'TX' },
	{ name: 'Winkler', abbreviation: 'WIR', state: 'TX' },
	{ name: 'Wise', abbreviation: 'WIS', state: 'TX' },
	{ name: 'Wood', abbreviation: 'WOO', state: 'TX' },
	{ name: 'Yoakum', abbreviation: 'YOA', state: 'TX' },
	{ name: 'Young', abbreviation: 'YOU', state: 'TX' },
	{ name: 'Zapata', abbreviation: 'ZAP', state: 'TX' },
	{ name: 'Zavala', abbreviation: 'ZAV', state: 'TX' },
	// NY
	{ name: 'Albany', abbreviation: 'ALB', state: 'NY' },
	{ name: 'Allegany', abbreviation: 'ALL', state: 'NY' },
	{ name: 'Bronx', abbreviation: 'BRX', state: 'NY' },
	{ name: 'Broome', abbreviation: 'BME', state: 'NY' },
	{ name: 'Cattaraugus', abbreviation: 'CAT', state: 'NY' },
	{ name: 'Cayuga', abbreviation: 'CAY', state: 'NY' },
	{ name: 'Chautauqua', abbreviation: 'CUA', state: 'NY' },
	{ name: 'Chemung', abbreviation: 'CHG', state: 'NY' },
	{ name: 'Chenango', abbreviation: 'CGO', state: 'NY' },
	{ name: 'Clinton', abbreviation: 'CLI', state: 'NY' },
	{ name: 'Columbia', abbreviation: 'COA', state: 'NY' },
	{ name: 'Cortland', abbreviation: 'COD', state: 'NY' },
	{ name: 'Delaware', abbreviation: 'DEE', state: 'NY' },
	{ name: 'Dutchess', abbreviation: 'DUT', state: 'NY' },
	{ name: 'Erie', abbreviation: 'ERI', state: 'NY' },
	{ name: 'Essex', abbreviation: 'ESS', state: 'NY' },
	{ name: 'Franklin', abbreviation: 'FIN', state: 'NY' },
	{ name: 'Fulton', abbreviation: 'FUL', state: 'NY' },
	{ name: 'Genesee', abbreviation: 'GEE', state: 'NY' },
	{ name: 'Greene', abbreviation: 'GNE', state: 'NY' },
	{ name: 'Hamilton', abbreviation: 'HMI', state: 'NY' },
	{ name: 'Herkimer', abbreviation: 'HRK', state: 'NY' },
	{ name: 'Jefferson', abbreviation: 'JFF', state: 'NY' },
	{ name: 'Kings', abbreviation: 'KIS', state: 'NY' },
	{ name: 'Lewis', abbreviation: 'LEW', state: 'NY' },
	{ name: 'Livingston', abbreviation: 'LIN', state: 'NY' },
	{ name: 'Madison', abbreviation: 'MDI', state: 'NY' },
	{ name: 'Monroe', abbreviation: 'MNR', state: 'NY' },
	{ name: 'Montgomery', abbreviation: 'MRY', state: 'NY' },
	{ name: 'Nassau', abbreviation: 'NAU', state: 'NY' },
	{ name: 'New York', abbreviation: 'NYK', state: 'NY' },
	{ name: 'Niagara', abbreviation: 'NIA', state: 'NY' },
	{ name: 'Oneida', abbreviation: 'ONE', state: 'NY' },
	{ name: 'Onondaga', abbreviation: 'ONO', state: 'NY' },
	{ name: 'Ontario', abbreviation: 'ONT', state: 'NY' },
	{ name: 'Orange', abbreviation: 'ORE', state: 'NY' },
	{ name: 'Orleans', abbreviation: 'ORL', state: 'NY' },
	{ name: 'Oswego', abbreviation: 'OSW', state: 'NY' },
	{ name: 'Otsego', abbreviation: 'OTS', state: 'NY' },
	{ name: 'Putnam', abbreviation: 'PUM', state: 'NY' },
	{ name: 'Queens', abbreviation: 'QUE', state: 'NY' },
	{ name: 'Rensselaer', abbreviation: 'REN', state: 'NY' },
	{ name: 'Richmond', abbreviation: 'RIC', state: 'NY' },
	{ name: 'Rockland', abbreviation: 'ROD', state: 'NY' },
	{ name: 'St. Lawrence', abbreviation: 'SLE', state: 'NY' },
	{ name: 'Saratoga', abbreviation: 'SAR', state: 'NY' },
	{ name: 'Schenectady', abbreviation: 'SCY', state: 'NY' },
	{ name: 'Schoharie', abbreviation: 'SCE', state: 'NY' },
	{ name: 'Schuyler', abbreviation: 'SCR', state: 'NY' },
	{ name: 'Seneca', abbreviation: 'SEN', state: 'NY' },
	{ name: 'Steuben', abbreviation: 'STN', state: 'NY' },
	{ name: 'Suffolk', abbreviation: 'SUF', state: 'NY' },
	{ name: 'Sullivan', abbreviation: 'SUL', state: 'NY' },
	{ name: 'Tioga', abbreviation: 'TIO', state: 'NY' },
	{ name: 'Tompkins', abbreviation: 'TOS', state: 'NY' },
	{ name: 'Ulster', abbreviation: 'ULS', state: 'NY' },
	{ name: 'Warren', abbreviation: 'WEN', state: 'NY' },
	{ name: 'Washington', abbreviation: 'WSH', state: 'NY' },
	{ name: 'Wayne', abbreviation: 'WAY', state: 'NY' },
	{ name: 'Westchester', abbreviation: 'WES', state: 'NY' },
	{ name: 'Wyoming', abbreviation: 'WYO', state: 'NY' },
	{ name: 'Yates', abbreviation: 'YAT', state: 'NY' },
	// CO
	{ name: 'Adams', abbreviation: 'ADA', state: 'CO' },
	{ name: 'Alamosa', abbreviation: 'ALA', state: 'CO' },
	{ name: 'Arapahoe', abbreviation: 'ARE', state: 'CO' },
	{ name: 'Archuleta', abbreviation: 'ACH', state: 'CO' },
	{ name: 'Baca', abbreviation: 'BAC', state: 'CO' },
	{ name: 'Bent', abbreviation: 'BEN', state: 'CO' },
	{ name: 'Boulder', abbreviation: 'BOU', state: 'CO' },
	{ name: 'Broomfield', abbreviation: 'BRD', state: 'CO' },
	{ name: 'Chaffee', abbreviation: 'CEE', state: 'CO' },
	{ name: 'Cheyenne', abbreviation: 'CNE', state: 'CO' },
	{ name: 'Clear Creek', abbreviation: 'CCK', state: 'CO' },
	{ name: 'Conejos', abbreviation: 'COS', state: 'CO' },
	{ name: 'Costilla', abbreviation: 'CST', state: 'CO' },
	{ name: 'Crowley', abbreviation: 'CEY', state: 'CO' },
	{ name: 'Custer', abbreviation: 'CUS', state: 'CO' },
	{ name: 'Delta', abbreviation: 'DTA', state: 'CO' },
	{ name: 'Denver', abbreviation: 'DER', state: 'CO' },
	{ name: 'Dolores', abbreviation: 'DOL', state: 'CO' },
	{ name: 'Douglas', abbreviation: 'DOU', state: 'CO' },
	{ name: 'Eagle', abbreviation: 'EAG', state: 'CO' },
	{ name: 'Elbert', abbreviation: 'ELB', state: 'CO' },
	{ name: 'El Paso', abbreviation: 'EPO', state: 'CO' },
	{ name: 'Fremont', abbreviation: 'FRT', state: 'CO' },
	{ name: 'Garfield', abbreviation: 'GLD', state: 'CO' },
	{ name: 'Gilpin', abbreviation: 'GIN', state: 'CO' },
	{ name: 'Grand', abbreviation: 'GRD', state: 'CO' },
	{ name: 'Gunnison', abbreviation: 'GUN', state: 'CO' },
	{ name: 'Hinsdale', abbreviation: 'HIE', state: 'CO' },
	{ name: 'Huerfano', abbreviation: 'HUE', state: 'CO' },
	{ name: 'Jackson', abbreviation: 'JCK', state: 'CO' },
	{ name: 'Jefferson', abbreviation: 'JEE', state: 'CO' },
	{ name: 'Kiowa', abbreviation: 'KIO', state: 'CO' },
	{ name: 'Kit Carson', abbreviation: 'KCN', state: 'CO' },
	{ name: 'Lake', abbreviation: 'LKE', state: 'CO' },
	{ name: 'La Plata', abbreviation: 'LPA', state: 'CO' },
	{ name: 'Larimer', abbreviation: 'LAR', state: 'CO' },
	{ name: 'Las Animas', abbreviation: 'LSA', state: 'CO' },
	{ name: 'Lincoln', abbreviation: 'LLN', state: 'CO' },
	{ name: 'Logan', abbreviation: 'LOG', state: 'CO' },
	{ name: 'Mesa', abbreviation: 'MES', state: 'CO' },
	{ name: 'Mineral', abbreviation: 'MAL', state: 'CO' },
	{ name: 'Moffat', abbreviation: 'MOF', state: 'CO' },
	{ name: 'Montezuma', abbreviation: 'MOA', state: 'CO' },
	{ name: 'Montrose', abbreviation: 'MSE', state: 'CO' },
	{ name: 'Morgan', abbreviation: 'MRG', state: 'CO' },
	{ name: 'Otero', abbreviation: 'OTE', state: 'CO' },
	{ name: 'Ouray', abbreviation: 'OUR', state: 'CO' },
	{ name: 'Park', abbreviation: 'PAK', state: 'CO' },
	{ name: 'Phillips', abbreviation: 'PHI', state: 'CO' },
	{ name: 'Pitkin', abbreviation: 'PIT', state: 'CO' },
	{ name: 'Prowers', abbreviation: 'PRO', state: 'CO' },
	{ name: 'Pueblo', abbreviation: 'PUE', state: 'CO' },
	{ name: 'Rio Blanco', abbreviation: 'RBO', state: 'CO' },
	{ name: 'Rio Grande', abbreviation: 'RGE', state: 'CO' },
	{ name: 'Routt', abbreviation: 'ROU', state: 'CO' },
	{ name: 'Saguache', abbreviation: 'SAG', state: 'CO' },
	{ name: 'San Juan', abbreviation: 'SNJ', state: 'CO' },
	{ name: 'San Miguel', abbreviation: 'SML', state: 'CO' },
	{ name: 'Sedgwick', abbreviation: 'SED', state: 'CO' },
	{ name: 'Summit', abbreviation: 'SIT', state: 'CO' },
	{ name: 'Teller', abbreviation: 'TEL', state: 'CO' },
	{ name: 'Washington', abbreviation: 'WAH', state: 'CO' },
	{ name: 'Weld', abbreviation: 'WEL', state: 'CO' },
	{ name: 'Yuma', abbreviation: 'YUM', state: 'CO' },
];
